import { storeToRefs } from 'pinia'
import { onBeforeUnmount, toRaw, watchEffect } from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { useCoreStore } from '@/core/store/useCoreStore'
import { VITE_OLYMPUS_URL } from '@/OlympusClient'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

import {
  buildHestiaIframeAuthUrl,
  getHestiaUrl,
  toHestiaPostMessagePayload,
} from './UseHestiaIframe'

const TEARDOWN_DELAY_MS = 10_000

/**
 * Loads Hestia's iframe-auth page in a hidden iframe after the browser goes
 * idle, so JS bundles, fonts, and the first round of authenticated API
 * responses are warm by the time the user clicks the Owner Agent FAB or
 * opens the website builder.
 *
 * This is the same handshake a visible widget iframe runs — load
 * /iframe-auth, wait for HESTIA_IFRAME_READY, post a real
 * HESTIA_SESSION_HYDRATION, let Hestia redirect into the real route. The
 * only differences are the iframe is invisible and we tear it down on a
 * timer once warming is done.
 *
 * Only fires once the active Hermes user has finished authenticating
 * (csrfToken + user populated). Before that point we had no session to
 * send, so Hestia's provider chain (AuthProvider, BrandLocation, etc.)
 * would mount with no auth and its eager queries would 401 — that's why
 * the previous implementation used a `?preload` short-circuit to skip
 * the handshake entirely, and why the flag got turned off. Gating on
 * auth lets us go back to a real handshake against an authenticated
 * request graph.
 */
export const useHestiaPreload = (): void => {
  const featureFlags = useFeatureFlags()
  const hestiaUrl = getHestiaUrl()
  const expectedOrigin = new URL(hestiaUrl).origin

  const { csrfToken, user, isMultiBrandUser } = storeToRefs(useAuthStore())
  const { activeBrandId } = storeToRefs(useCoreStore())

  let iframe: HTMLIFrameElement | null = null
  let teardownId: number | null = null

  // Parent half of the standard Hestia iframe handshake (same shape as
  // useHestiaIframe). Sibling Hestia iframes share an origin, so the
  // event.source match against our specific iframe is what scopes the
  // message to us.
  const handleMessage = (event: MessageEvent): void => {
    if (event.origin !== expectedOrigin) return
    const iframeWindow = iframe?.contentWindow
    if (!iframeWindow || event.source !== iframeWindow) return
    if (event.data?.type !== 'HESTIA_IFRAME_READY') return
    if (!csrfToken.value || !user.value) return

    iframeWindow.postMessage(
      toHestiaPostMessagePayload({
        session: {
          apiUrl: VITE_OLYMPUS_URL,
          audience: 'hermes' as const,
          brandId: activeBrandId.value,
          csrfToken: csrfToken.value,
          isMultiBrandUser: isMultiBrandUser.value,
          user: toRaw(user.value),
        },
        type: 'HESTIA_SESSION_HYDRATION',
      }),
      expectedOrigin
    )
  }

  const cleanup = (): void => {
    if (teardownId != null) {
      clearTimeout(teardownId)
      teardownId = null
    }
    window.removeEventListener('message', handleMessage)
    iframe?.remove()
    iframe = null
  }

  const createPreloadIframe = (): void => {
    window.addEventListener('message', handleMessage)
    iframe = document.createElement('iframe')
    iframe.src = buildHestiaIframeAuthUrl({ hestiaUrl })
    iframe.setAttribute('aria-hidden', 'true')
    iframe.setAttribute('tabindex', '-1')
    iframe.style.cssText =
      'position:fixed;width:0;height:0;border:none;opacity:0;pointer-events:none;'
    document.body.appendChild(iframe)
    teardownId = window.setTimeout(cleanup, TEARDOWN_DELAY_MS)
  }

  // Run once the user is authed AND the flag is on. The didFire flag
  // self-disables subsequent re-fires (e.g. csrfToken rotation) since we
  // only need to warm the cache once per session.
  let didFire = false
  let idleId: number | null = null
  const stop = watchEffect(() => {
    if (didFire) return
    if (!csrfToken.value || !user.value) return
    if (!featureFlags.isEnabled('hermes.hestiaPreload')) return
    didFire = true
    const fire = (): void => {
      idleId = null
      createPreloadIframe()
    }
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(fire, { timeout: 5000 })
    } else {
      idleId = window.setTimeout(fire, 3000) as unknown as number
    }
  })

  onBeforeUnmount(() => {
    stop()
    // Cancel any pending idle callback so we don't append an orphaned
    // iframe + message listener after the component has unmounted.
    if (idleId != null) {
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId)
      } else {
        clearTimeout(idleId)
      }
      idleId = null
    }
    cleanup()
  })
}
